//ApplicationTypeList.tsx
//申請種別一覧画面
import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Avatar,
  Chip,
  Checkbox,
  TextField,
  Tooltip,
  RadioGroup,
  FormControlLabel,
  Radio,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  InputAdornment,
  useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ClearSharpIcon from '@mui/icons-material/ClearSharp';
import { ExpandMore as ChevronDown, ExpandLess as ChevronUp } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { ApplicationType } from '../data/ApplicationType';
import { iconMap } from '../../HomePage/Components/ApplicationSelect/iconMap';
import ApplicationTypeDialog from './ApplicationTypeDialog';
import theme from '../../../style/theme';
import { getApplicationTypes, deleteApplicationType, createApplicationType, updateApplicationType } from '../../../service/ApplicationTypeService';

// テーブルのスタイルを設定
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.custom.gray,
    color: theme.palette.common.white,
    whiteSpace: 'nowrap',
    padding: '6px 20px',
  },
  [`&.${tableCellClasses.body}`]: {
    whiteSpace: 'nowrap',
    padding: '6px 20px',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const columnLabels: Record<string, string> = {
  typeName: '種別名',
  description: '説明',
  themeName: 'テーマ',
};

/**
 * 申請種別一覧コンポーネント
 * - 申請種別を検索・ソート・ページング付きのテーブルで一覧表示する
 * - 行クリックで編集ダイアログを開き、チェックボックスで一括削除できる
 */
export default function ApplicationTypeList() {
  const isMobile = useMediaQuery('(max-width:600px)');
  const [applicationTypes, setApplicationTypes] = useState<ApplicationType[]>([]);
  const [availableThemes, setAvailableThemes] = useState<ApplicationType['theme'][]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedType, setSelectedType] = useState<ApplicationType | null>(null);
  const [loading, setLoading] = useState(true);

  // 検索とフィルタリング
  const [searchTerm, setSearchTerm] = useState(() => {
    return sessionStorage.getItem('searchText_ApplicationType') || '';
  });
  const [sortConfig, setSortConfig] = useState<{ key: string; asc: boolean }>({
    key: 'typeName',
    asc: true,
  });
  const [hoveredColumn, setHoveredColumn] = useState<string | null>(null);

  // 表示設定
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
    const stored = sessionStorage.getItem('visibleColumns_ApplicationType');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          return new Set<string>(parsed);
        }
      } catch (e) {
        console.warn('visibleColumns の復元に失敗しました:', e);
      }
    }
    return new Set<string>(Object.keys(columnLabels));
  });
  const [showColumnSettings, setShowColumnSettings] = useState(false);

  // ページング
  const defaultRowPerPage = 50;
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    return Number(sessionStorage.getItem('showItems_ApplicationType')) || defaultRowPerPage;
  });
  const rowPerPageList = [50, 100, 1000];
  const [page, setPage] = useState(() => {
    const stored = sessionStorage.getItem('paging_ApplicationType');
    const parsed = stored ? parseInt(stored, 10) : NaN;
    return !isNaN(parsed) && parsed > 0 ? parsed : 1;
  });

  // 検索設定
  const [showSearchSettings, setShowSearchSettings] = useState(false);
  const [columnSearchTerms, setColumnSearchTerms] = useState<Record<string, string>>({});

  // 選択と削除
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // 初期データ取得
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const { applicationTypes, availableThemes } = await getApplicationTypes();
      setApplicationTypes(applicationTypes);
      setAvailableThemes(availableThemes);
      setLoading(false);
    };
    fetchData();
  }, []);

  // 検索設定をsessionStorageから復元
  useEffect(() => {
    const savedTerms = sessionStorage.getItem('columnSearchTerms_ApplicationType');
    if (savedTerms) {
      try {
        const parsedTerms = JSON.parse(savedTerms);
        if (typeof parsedTerms === 'object' && parsedTerms !== null) {
          const sanitizedTerms = Object.fromEntries(
            Object.entries(parsedTerms).filter(
              ([_, v]) => typeof v === 'string' && v !== ''
            )
          ) as Record<string, string>;
          setColumnSearchTerms(sanitizedTerms);
        }
      } catch (e) {
        console.warn('Invalid sessionStorage value for columnSearchTerms');
      }
    }
  }, []);

  // 列の表示切替
  const toggleColumn = (key: string) => {
    const newSet = new Set(visibleColumns);
    newSet.has(key) ? newSet.delete(key) : newSet.add(key);
    sessionStorage.setItem('visibleColumns_ApplicationType', JSON.stringify(Array.from(newSet)));
    setVisibleColumns(newSet);
  };

  // フィルタリングとソート
  const filteredData = useMemo(() => {
    let result = [...applicationTypes];

    // 全体検索キーワード
    const keywords = searchTerm.toLowerCase().split(/\s+/).filter(Boolean);
    if (keywords.length > 0) {
      result = result.filter((row) =>
        keywords.every((keyword) =>
          Object.values(row).some((value) => {
            if (typeof value === 'string') {
              return value.toLowerCase().includes(keyword);
            }
            if (typeof value === 'object' && value !== null) {
              return Object.values(value).some(
                (v) => typeof v === 'string' && v.toLowerCase().includes(keyword)
              );
            }
            return false;
          })
        )
      );
    }

    // 項目別検索
    const activeColumnSearches = Object.entries(columnSearchTerms).filter(
      ([_, value]) => value.trim() !== ''
    );
    if (activeColumnSearches.length > 0) {
      result = result.filter((row) =>
        activeColumnSearches.every(([key, value]) => {
          if (key === 'themeName') {
            return row.theme.themeName.toLowerCase().includes(value.toLowerCase());
          }

          const cellValue = row[key as keyof ApplicationType];
          return typeof cellValue === 'string' && cellValue.toLowerCase().includes(value.toLowerCase());
        })
      );
    }

    // ソート処理
    const { key, asc } = sortConfig;
    result.sort((a, b) => {
      let aValue: string;
      let bValue: string;

      if (key === 'themeName') {
        aValue = a.theme.themeName;
        bValue = b.theme.themeName;
      } else {
        aValue = String(a[key as keyof ApplicationType] || '');
        bValue = String(b[key as keyof ApplicationType] || '');
      }

      return asc ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });

    setPage(1);
    return result;
  }, [searchTerm, columnSearchTerms, applicationTypes, sortConfig]);

  // ページング
  const paginatedData = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  // 列別検索の変更処理
  const handleColumnSearchChange = (key: string, value: string) => {
    const updated = {
      ...columnSearchTerms,
      [key]: value,
    };
    setColumnSearchTerms(updated);
    const allEmpty = Object.values(updated).every((v) => v === '');
    if (allEmpty) {
      sessionStorage.removeItem('columnSearchTerms_ApplicationType');
    } else {
      sessionStorage.setItem('columnSearchTerms_ApplicationType', JSON.stringify(updated));
    }
  };

  // フィルターリセット
  const filterReset = () => {
    sessionStorage.removeItem('searchText_ApplicationType');
    sessionStorage.removeItem('showItems_ApplicationType');
    sessionStorage.removeItem('visibleColumns_ApplicationType');
    sessionStorage.removeItem('columnSearchTerms_ApplicationType');
    sessionStorage.removeItem('paging_ApplicationType');

    setSearchTerm('');
    setVisibleColumns(new Set(Object.keys(columnLabels)));
    setRowsPerPage(defaultRowPerPage);
    setColumnSearchTerms({});
    setPage(1);
  };

  // テキストボックスのテキストをクリア
  const handleFilterTextClear = () => {
    setSearchTerm('');
    sessionStorage.removeItem('searchText_ApplicationType');
  };

  // 編集ボタンクリック
  const handleEdit = (type: ApplicationType) => {
    setSelectedType(type);
    setOpenDialog(true);
  };

  // 削除処理
  const handleDelete = async () => {
    const deletePromises = Array.from(selectedRows).map((id) => deleteApplicationType(id));
    const results = await Promise.all(deletePromises);

    if (results.every((result) => result)) {
      // 削除成功後、データを再取得
      const { applicationTypes, availableThemes } = await getApplicationTypes();
      setApplicationTypes(applicationTypes);
      setAvailableThemes(availableThemes);
      setSelectedRows(new Set());
      setShowDeleteDialog(false);
    } else {
      alert('削除に失敗しました');
    }
  };

  // 新規追加ボタンクリック
  const handleAdd = () => {
    setSelectedType(null);
    setOpenDialog(true);
  };

  // ダイアログ保存処理
  const handleSave = async (updatedType: ApplicationType) => {
    let success = false;

    if (selectedType) {
      // 既存データ更新
      success = await updateApplicationType(
        updatedType.id,
        updatedType.typeName,
        updatedType.description,
        updatedType.theme.themeId
      );

      if (success) {
        // 更新成功後、データを再取得
        const { applicationTypes, availableThemes } = await getApplicationTypes();
        setApplicationTypes(applicationTypes);
        setAvailableThemes(availableThemes);
      } else {
        alert('更新に失敗しました');
        return;
      }
    } else {
      // 新規追加
      success = await createApplicationType(
        updatedType.typeName,
        updatedType.description,
        updatedType.theme.themeId
      );

      if (success) {
        // 作成成功後、データを再取得
        const { applicationTypes, availableThemes } = await getApplicationTypes();
        setApplicationTypes(applicationTypes);
        setAvailableThemes(availableThemes);
      } else {
        alert('作成に失敗しました');
        return;
      }
    }

    setOpenDialog(false);
  };

  // ダイアログキャンセル処理
  const handleCancel = () => {
    setOpenDialog(false);
    // ダイアログの閉じるアニメーション後にselectedTypeをクリア
  };

  // テーマ追加後のデータ再取得
  const handleThemeAdded = async () => {
    const { applicationTypes, availableThemes } = await getApplicationTypes();
    setApplicationTypes(applicationTypes);
    setAvailableThemes(availableThemes);
  };

  return (
    <div className="p-4">
      <Box display="flex" alignItems="center" gap={2}>
        {/* 検索欄 */}
        <div className="mb-2 flex items-center gap-2">
          <TextField
            label="検索"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => {
              const value = e.target.value;
              setSearchTerm(value);
              if (value === '') {
                sessionStorage.removeItem('searchText_ApplicationType');
              } else {
                sessionStorage.setItem('searchText_ApplicationType', value);
              }
            }}
            className="w-64"
            sx={{ ml: 1, mt: 2, width: 220 }}
            slotProps={{
              input: {
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleFilterTextClear}
                      edge="end"
                      sx={{
                        backgroundColor: 'transparent',
                        '&:hover': {
                          backgroundColor: 'transparent',
                        },
                      }}
                    >
                      <ClearSharpIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </div>
        {/* リセットボタン */}
        <Chip
          label="リセット"
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
            ml: 1,
            mt: 2,
          }}
          onClick={filterReset}
        />
      </Box>

      {/* 表示設定 */}
      <Button
        sx={{ ml: 1, mt: 1, typography: 'buttonMedium' }}
        onClick={() => setShowColumnSettings(!showColumnSettings)}
        startIcon={showColumnSettings ? <ChevronUp /> : <ChevronDown />}
      >
        表示設定
      </Button>

      <Box sx={{ pl: 2, pb: 1 }}>
        {showColumnSettings && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {/* 表示件数 */}
            <Box sx={{ mb: 0 }}>
              <div style={{ fontWeight: 'bold', color: 'grey', marginBottom: -6 }}>表示件数:</div>
              <RadioGroup
                row
                value={rowsPerPage.toString()}
                onChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value));
                  sessionStorage.setItem('showItems_ApplicationType', e.target.value);
                  setPage(1);
                  sessionStorage.removeItem('paging_ApplicationType');
                }}
              >
                {rowPerPageList.map((num) => (
                  <FormControlLabel
                    key={num}
                    value={num.toString()}
                    control={<Radio sx={{ transform: 'scale(0.6)' }} />}
                    label={`${num}件`}
                  />
                ))}
              </RadioGroup>
            </Box>

            {/* 表示項目 */}
            <Box>
              <div className="font-semibold mb-2" style={{ fontWeight: 'bold', color: 'grey', marginBottom: 4 }}>
                表示項目:
              </div>
              <Box className="flex flex-wrap gap-2">
                {Object.entries(columnLabels).map(([key, label]) => (
                  <label key={key} className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={visibleColumns.has(key)}
                      onChange={() => toggleColumn(key)}
                    />
                    {label}
                  </label>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* 検索設定 */}
      <Button
        sx={{ ml: 1, mt: -1, typography: 'buttonMedium' }}
        onClick={() => setShowSearchSettings(!showSearchSettings)}
        startIcon={showSearchSettings ? <ChevronUp /> : <ChevronDown />}
      >
        検索設定
      </Button>

      <Box sx={{ pl: 2, pb: 2 }}>
        {showSearchSettings && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {Object.entries(columnLabels).map(([key, label]) => (
              <Box key={key} sx={{ display: 'flex', flexDirection: 'column', minWidth: 100 }}>
                <div style={{ fontSize: '1rem', marginBottom: 4 }}>{label}</div>
                <TextField
                  size="small"
                  variant="outlined"
                  value={columnSearchTerms[key] || ''}
                  onChange={(e) => handleColumnSearchChange(key, e.target.value)}
                  sx={{
                    width: 140,
                    '& input': {
                      padding: '4px 8px',
                    },
                  }}
                  InputProps={{
                    startAdornment: columnSearchTerms[key] ? undefined : (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* 新規作成ボタンと削除ボタン */}
      <Box sx={{ mt: 0, mb: 0, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          sx={{ ml: 1 }}
          onClick={handleAdd}
        >
          申請種別新規追加
        </Button>
        {selectedRows.size > 0 && (
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setShowDeleteDialog(true)}
          >
            削除
          </Button>
        )}
      </Box>

      {/* ページング */}
      <Box sx={{ ml: 2, my: 1, display: 'flex', gap: 2, justifyContent: 'flex-start', alignItems: 'center' }}>
        <div style={{ marginBottom: 1, color: '#666', fontSize: '1rem' }}>
          全 {filteredData.length} 件中{' '}
          {filteredData.length === 0
            ? '0 件 〜 0 件'
            : `${(page - 1) * rowsPerPage + 1} 件 〜 ${Math.min(page * rowsPerPage, filteredData.length)} 件`}{' '}
          を表示
        </div>
        <div>
          <Pagination
            count={Math.ceil(filteredData.length / rowsPerPage)}
            page={page}
            onChange={(e, value) => {
              setPage(value);
              sessionStorage.setItem('paging_ApplicationType', value.toString());
            }}
            color="primary"
            sx={{
              '& .MuiPaginationItem-root': {
                minWidth: '20px',
                height: '20px',
                padding: '1px',
              },
            }}
          />
        </div>
      </Box>

      {/* テーブル */}
      <TableContainer
        component={Paper}
        sx={{
          ml: isMobile ? 0 : 1,
          mb: 2,
          overflowX: 'auto',
          maxWidth: '100%',
        }}
      >
        <Table sx={{ tableLayout: 'auto', minWidth: 800 }} aria-label="customized table">
          <TableHead>
            <TableRow>
              {/* チェックボックス列 */}
              <StyledTableCell>
                <Tooltip title={selectedRows.size === applicationTypes.length ? 'すべて選択解除' : 'すべて選択'}>
                  <Checkbox
                    checked={selectedRows.size === applicationTypes.length && applicationTypes.length > 0}
                    onChange={() => {
                      if (selectedRows.size === applicationTypes.length) {
                        setSelectedRows(new Set());
                      } else {
                        setSelectedRows(new Set(applicationTypes.map((row) => row.id)));
                      }
                    }}
                  />
                </Tooltip>
              </StyledTableCell>

              {/* 動的列ヘッダー */}
              {Object.entries(columnLabels).map(([key, label]) => {
                if (!visibleColumns.has(key)) return null;
                const isActive = sortConfig?.key === key;
                const asc = sortConfig?.asc;

                return (
                  <StyledTableCell
                    key={key}
                    onClick={() => {
                      setPage(1);
                      setSortConfig((prev) =>
                        prev?.key === key ? { key, asc: !prev.asc } : { key, asc: true }
                      );
                    }}
                    onMouseEnter={() => setHoveredColumn(key)}
                    onMouseLeave={() => setHoveredColumn(null)}
                    style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                      {label}
                      {(isActive || hoveredColumn === key) &&
                        (asc && isActive ? (
                          <ChevronUp fontSize="small" className="ml-1 text-gray-500" />
                        ) : !asc && isActive ? (
                          <ChevronDown fontSize="small" className="ml-1 text-gray-500" />
                        ) : (
                          <ChevronDown fontSize="small" className="ml-1 text-gray-300" />
                        ))}
                    </div>
                  </StyledTableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedData.map((row) => {
              const IconComponent = iconMap[row.theme.iconName] || iconMap['MoreHorizRoundedIcon'];
              return (
                <StyledTableRow
                  key={row.id}
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest('input[type="checkbox"]')) return;
                    handleEdit(row);
                  }}
                  sx={{
                    height: '48px',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#e3f2fd',
                      boxShadow: 'inset 0 0 0 1px #90caf9',
                    },
                  }}
                  title="編集"
                >
                  {/* チェックボックスセル */}
                  <StyledTableCell>
                    <Checkbox
                      checked={selectedRows.has(row.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        const newSet = new Set(selectedRows);
                        if (newSet.has(row.id)) {
                          newSet.delete(row.id);
                        } else {
                          newSet.add(row.id);
                        }
                        setSelectedRows(newSet);
                      }}
                    />
                  </StyledTableCell>

                  {/* 種別名 */}
                  {visibleColumns.has('typeName') && (
                    <StyledTableCell>
                      <Typography variant="body1" fontWeight="bold">
                        {row.typeName}
                      </Typography>
                    </StyledTableCell>
                  )}

                  {/* 説明 */}
                  {visibleColumns.has('description') && (
                    <StyledTableCell>
                      <Typography variant="body2" color="text.secondary">
                        {row.description || '-'}
                      </Typography>
                    </StyledTableCell>
                  )}

                  {/* 応用テーマ */}
                  {visibleColumns.has('themeName') && (
                    <StyledTableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          sx={{
                            bgcolor: row.theme.iconBgColor,
                            width: 28,
                            height: 28,
                            border: `2px solid ${row.theme.iconBdColor || row.theme.iconBgColor}`,
                          }}
                        >
                          <IconComponent
                            style={{
                              color: row.theme.iconColor,
                              width: 18,
                              height: 18,
                            }}
                          />
                        </Avatar>
                        <Chip
                          label={row.theme.themeName}
                          size="small"
                          sx={{
                            bgcolor: row.theme.buttonColor,
                            color: row.theme.fontColor,
                            border: `1px solid ${row.theme.buttonBdColor || row.theme.buttonColor}`,
                            borderRadius: '6px',
                            height: '28px',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            px: 1.5,
                            '& .MuiChip-label': {
                              px: 0.5,
                            },
                          }}
                        />
                      </Box>
                    </StyledTableCell>
                  )}
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ページング（下部） */}
      <Box sx={{ ml: 2, mb: 1, display: 'flex', gap: 2, justifyContent: 'flex-start', alignItems: 'center' }}>
        <div style={{ marginTop: 1, color: '#666', fontSize: '1rem' }}>
          全 {filteredData.length} 件中{' '}
          {filteredData.length === 0
            ? '0 件 〜 0 件'
            : `${(page - 1) * rowsPerPage + 1} 件 〜 ${Math.min(page * rowsPerPage, filteredData.length)} 件`}{' '}
          を表示
        </div>
        <div>
          <Pagination
            count={Math.ceil(filteredData.length / rowsPerPage)}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
            sx={{
              '& .MuiPaginationItem-root': {
                minWidth: '20px',
                height: '20px',
                padding: '1px',
              },
            }}
          />
        </div>
      </Box>

      {/* 削除確認ダイアログ */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>削除確認</DialogTitle>
        <DialogContent>
          <DialogContentText>
            選択された {selectedRows.size} 件の申請種別を削除しますか?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>キャンセル</Button>
          <Button onClick={handleDelete} color="error">
            削除
          </Button>
        </DialogActions>
      </Dialog>

      {/* 詳細設定ダイアログ */}
      <ApplicationTypeDialog
        open={openDialog}
        applicationType={selectedType}
        availableThemes={availableThemes}
        onSave={handleSave}
        onCancel={handleCancel}
        onThemeAdded={handleThemeAdded}
        onExited={() => setSelectedType(null)}
      />
    </div>
  );
}
